import {
  DesiredSettingValue,
  DesiredSettingValueKind,
  Frequency,
} from '@crossnokaye/typescript-sdk/models/components';

import { RefValueKind, ScheduleEvent, SettingKind } from '@repo/sdk/models/components';

import { SequenceWithValues } from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { getSourceUnits } from '@/features/equipment-browser/utils';
import { castSettingsValue, getDesiredSettingValueKind } from '@/helpers/settings';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';
import { NO_VALUE } from '@/helpers/unit-formatter/unit-formatter.constants';

import { DetailsFormData } from './equipment-details-form.types';

/**
 * getInitialSettings
 *
 * Transforms an array of enhanced settings into initial form data for equipment settings.
 * Converts setting values from base units to display units and formats them for form input.
 * Returns empty string for settings with no value.
 * Used for both general settings and sequencer settings.
 *
 * @param settings - Array of enhanced settings with values and units
 * @returns Record of setting IDs mapped to formatted display values
 */
export const getInitialSettings = (settings: EnhancedSetting[]) => {
  return settings.reduce((acc: Record<string, any>, setting: EnhancedSetting) => {
    // If setting.units is not configured, assume the value is already in displayUnits
    const sourceUnits = getSourceUnits(setting as EnhancedSetting);
    const formattedValue = formatUnits(
      { value: setting.value, units: sourceUnits },
      setting.displayUnits
    );
    acc[setting.ref.id] = formattedValue.valueLabel !== NO_VALUE ? formattedValue.valueLabel : '';
    return acc;
  }, {});
};

/**
 * getInitialSequencer
 *
 * Transforms an array of sequences with values into initial sequencer table form data.
 * Extracts the table configuration from each sequence and maps it by sequence ID.
 *
 * @param sequencesWithValues - Array of sequences containing table configurations
 * @returns Record of sequence IDs mapped to their table structures
 */
export const getInitialSequencer = (
  sequencesWithValues: SequenceWithValues[]
): DetailsFormData['sequencer'] => {
  return sequencesWithValues?.reduce((acc: any, sequence: SequenceWithValues) => {
    if (sequence.ref?.id)
      acc[sequence.ref.id] = {
        table: sequence.value?.table ?? [],
      };
    return acc;
  }, {}) as DetailsFormData['sequencer'];
};

/**
 * getInitialDefrostSchedule
 *
 * Transforms defrost schedule configuration into initial form data with date objects.
 * Converts RRule format (byHour, byMinute, byDay) into a form-friendly structure with
 * Date objects for time pickers and event day arrays for recurrence patterns.
 *
 * @param defrostScheduleValues - Optional defrost schedule configuration with events
 * @returns Array of defrost events formatted for form inputs, or empty array if no schedule
 */
export const getInitialDefrostSchedule = (
  events: ScheduleEvent[]
): DetailsFormData['defrostSchedule'] => {
  if (!events || events.length === 0) return [];

  const defrostSchedule = events.map((event) => ({
    startTime: (() => {
      const hour = event.rrule.byHour?.[0] ?? 0;
      const minute = event.rrule.byMinute?.[0] ?? 0;
      const now = new Date();
      now.setHours(hour, minute, 0, 0);
      return now;
    })(),
    eventDays: event.rrule.byDay ?? [],
    endDate: event.rrule.until ?? undefined,
  }));

  return defrostSchedule as DetailsFormData['defrostSchedule'];
};

/**
 * getUpdatedSettings
 *
 * Converts dirty form fields into API-ready DesiredSettingValue objects for submission.
 * Handles unit conversion from display units back to base units and determines the appropriate
 * value kind for each setting. Supports both regular settings and sequence-type settings.
 *
 * @param settings - Array of enhanced settings containing metadata and current values
 * @param dirtySettings - Record of modified setting IDs and their new values
 * @returns Array of DesiredSettingValue objects ready for API submission
 */
export const getUpdatedSettings = (
  settings: EnhancedSetting[],
  dirtySettings: Record<string, any>
) => {
  // O(N) once instead of O(N·D) per dirty key. Runs every keystroke via
  // `form.watch()` in `useDetailsSettingsSave`, so the lookup cost matters.
  const settingsById = new Map(settings?.map((s) => [s.ref.id, s]) ?? []);

  // Create array of DesiredSettingValue objects from dirty fields.
  // A dirty key may not have a matching entry in `settings` if the
  // displaySettings list has drifted from the form's dirty fields (e.g. a
  // websocket update removed/replaced a setting while the user still has a
  // pending edit). `buildChangesForSection` already skips these by id, so
  // returning null here keeps the two layers symmetric instead of crashing
  // on a casted-undefined access inside getSourceUnits.
  const updatedSettings: DesiredSettingValue[] = Object.entries(dirtySettings)
    .map(([settingKey, newValue]): DesiredSettingValue | null => {
      const setting = settingsById.get(settingKey);
      if (!setting) return null;

      if (setting.ref.valueKind === RefValueKind.Sequence) {
        return {
          settingId: settingKey,
          kind: DesiredSettingValueKind.Sequence,
          desiredValue: {
            [setting.ref.valueKind as string]: newValue,
          },
        };
      }

      // reverse the display units to the base units before saving
      // If setting.units is not configured, assume the value is already in the correct units
      const targetUnits = getSourceUnits(setting);
      const formattedValue = formatUnits(
        { value: newValue, units: setting.displayUnits },
        targetUnits
      );
      const kind: DesiredSettingValueKind = getDesiredSettingValueKind(setting.ref.valueKind);

      let valueLabel = formattedValue.valueLabel;

      if (setting.displayUnits?.toUpperCase() === 'PSIG') {
        valueLabel = String(newValue);
      }

      return {
        settingId: settingKey,
        kind: kind,
        desiredValue: {
          [setting.ref.valueKind as string]: castSettingsValue(valueLabel, setting.ref.valueKind),
        },
      } as DesiredSettingValue;
    })
    .filter((value): value is DesiredSettingValue => value !== null);
  return updatedSettings;
};

/**
 * getUpdatedSequencer
 *
 * Converts dirty sequencer table data into API-ready DesiredSettingValue objects.
 * Each sequencer is wrapped with the appropriate kind and value structure for submission.
 *
 * @param dirtySequencer - Record of modified sequencer IDs and their new table configurations
 * @returns Array of DesiredSettingValue objects for sequencer updates
 */
export const getUpdatedSequencer = (dirtySequencer: DetailsFormData['sequencer']) => {
  return Object.entries(dirtySequencer).map(([sequenceKey, sequenceValue]) => {
    return {
      settingId: sequenceKey,
      kind: DesiredSettingValueKind.Sequence,
      desiredValue: {
        [DesiredSettingValueKind.Sequence]: sequenceValue,
      },
    } as DesiredSettingValue;
  });
};

/**
 * getUpdatedDefrostSchedule
 *
 * Converts defrost schedule form data back into API-ready RRule format for submission.
 * Transforms Date objects and event day arrays into the RRule structure with frequency,
 * byHour, byMinute, byDay, and optional end date.
 *
 * @param defrostSettingId - ID of the defrost schedule setting to update
 * @param defrostSchedule - Array of defrost events from the form
 * @returns DesiredSettingValue object containing the complete schedule configuration
 */
export const getUpdatedDefrostSchedule = (
  defrostSettingId: string,
  defrostSchedule: DetailsFormData['defrostSchedule']
) => {
  const newEvents = defrostSchedule.map((event) => {
    const hour = event?.startTime?.getHours() ?? 0;
    const minute = event?.startTime?.getMinutes() ?? 0;

    return {
      rrule: {
        frequency: Frequency.Daily,
        until: event?.endDate ?? undefined,
        byDay: event?.eventDays,
        byHour: [hour],
        byMinute: [minute],
      },
    };
  });

  return {
    settingId: defrostSettingId,
    kind: SettingKind.Schedule,
    desiredValue: { schedule: { events: newEvents } },
  } as DesiredSettingValue;
};
